import type { ShellStore } from "@observe-tech/marlin-store/contract";
import { SHELL_API_VERSION } from "@observe-tech/mfe-shell-api";
import {
  hashKey,
  QueryClient,
  QueryClientContext,
  useQuery,
  type QueryKey,
} from "@tanstack/react-query";
import { useContext, useEffect, useRef } from "react";
import { getShellSession } from "../utils/shellSession";

const readStore = (): ShellStore | Error => {
  const shell = getShellSession();
  if (!shell) return new Error("The shell handle is not available");
  const { store, apiVersion } = shell;
  if (!store || apiVersion === undefined || apiVersion < SHELL_API_VERSION)
    return new Error(
      `The shell build does not match this MFE: the shell provides API version ${apiVersion ?? "none"} and this MFE needs version ${SHELL_API_VERSION}`,
    );
  return store;
};

export const getStore = (): ShellStore => {
  const store = readStore();
  if (store instanceof Error) throw store;
  return store;
};

export const hasStore = (): boolean => !(readStore() instanceof Error);

type CachedRead<T> = { value: T | undefined; error?: Error };

type Subscription = { refs: number; stop(): void };

let fallbackClient: QueryClient | undefined;
let lastStoreToken = 0;
const storeTokens = new WeakMap<ShellStore, number>();
const subscriptions = new WeakMap<QueryClient, Map<string, Subscription>>();

const storeToken = (store: ShellStore): number => {
  const known = storeTokens.get(store);
  if (known !== undefined) return known;
  lastStoreToken += 1;
  storeTokens.set(store, lastStoreToken);
  return lastStoreToken;
};

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

export const useStoreQuery = <T>(
  queryKey: QueryKey,
  querier: (store: ShellStore) => T | Promise<T>,
): { data: T | undefined; isPending: boolean; error: Error | null } => {
  const store = getStore();
  const client =
    useContext(QueryClientContext) ?? (fallbackClient ??= new QueryClient());
  const querierRef = useRef(querier);
  querierRef.current = querier;
  const effectiveKey: QueryKey = [
    "marlin-store",
    storeToken(store),
    ...queryKey,
  ];
  const keyHash = hashKey(effectiveKey);
  const query = useQuery<CachedRead<T>>(
    {
      queryKey: effectiveKey,
      queryFn: async () => {
        try {
          return { value: await querierRef.current(store) };
        } catch (error) {
          return { value: undefined, error: toError(error) };
        }
      },
      staleTime: Infinity,
      retry: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
    client,
  );

  useEffect(() => {
    const registry =
      subscriptions.get(client) ?? new Map<string, Subscription>();
    subscriptions.set(client, registry);
    let joined = registry.get(keyHash);
    if (!joined) {
      const subscription = store
        .observe(() => querierRef.current(store))
        .subscribe({
          next: (value) => {
            void client.cancelQueries({ queryKey: effectiveKey, exact: true });
            client.setQueryData<CachedRead<T>>(effectiveKey, { value });
          },
          error: (error) => {
            client.setQueryData<CachedRead<T>>(effectiveKey, (previous) => ({
              value: previous?.value,
              error: toError(error),
            }));
          },
        });
      joined = { refs: 0, stop: () => subscription.unsubscribe() };
      registry.set(keyHash, joined);
    }
    const entry = joined;
    entry.refs += 1;
    return () => {
      entry.refs -= 1;
      if (entry.refs > 0) return;
      entry.stop();
      if (registry.get(keyHash) === entry) registry.delete(keyHash);
    };
  }, [client, store, keyHash]);

  return {
    data: query.data?.value,
    isPending: query.isPending,
    error: query.data?.error ?? null,
  };
};
